import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, filter, switchMap, takeUntil, timeout } from 'rxjs/operators';
import { OnboardingChecklistService, OnboardingState } from '../onboarding-checklist.service';
import {
  ActivationJourneyService,
  ActivationJourneyState,
  ExploreStopId,
  activationStepToSlug,
  isFirstEmployeeCollectingPause,
} from '../activation-journey.service';
import { ExampleWorkdayService } from '../../example-workday/example-workday.service';
import { ActivationDestinationService, ActivationDestination } from '../activation-destination.service';
import { TokenEncryptionService } from 'src/app/Services/token-encryption.service';
import { SharedService } from 'src/app/Services/sharedService';
import { EntitlementsService } from 'src/app/Services/entitlements.service';
import { hasCompanyRole } from 'src/app/utils/role.utils';

interface ExploreStopDef {
  id: ExploreStopId;
  label: string;
  readyLabel?: string;
  hint?: string;
  pendingHint?: string;
  readyHint?: string;
  icon: string;
  route: string | null;
}

@Component({
  selector: 'app-floating-checklist',
  templateUrl: './floating-checklist.component.html',
  styleUrl: './floating-checklist.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FloatingChecklistComponent implements OnInit, OnDestroy {
  state: OnboardingState;
  journeyState: ActivationJourneyState | null = null;
  expanded: boolean = true;
  currentRoute: string = '';
  isLoggedIn: boolean = false;
  isTrialUser: boolean = false;
  isHiddenForSession: boolean = false;
  isEligible: boolean = true;
  isCompanyAdmin: boolean = false;
  shouldShow: boolean = false;
  showPrivateEvalBanner = false;
  showJourneyResumeBanner = false;
  resumeDestination: ActivationDestination | null = null;
  exploreCollapsed = false;
  showWhatYouControl = false;
  ownWorkdayReady = false;
  showExampleTrendsPanel = false;
  private orientationSeen = false;
  private subscription: Subscription;
  private journeySubscription: Subscription;
  private routerSubscription: Subscription;
  private hideSessionSubscription: Subscription;
  private eligibilitySubscription: Subscription;
  private explorePromptShownTracked = false;
  /**
   * Emits once ngOnDestroy fires. Every background sync subscription this
   * component owns pipes through `takeUntil(this.destroy$)` so pending HTTP
   * requests are cancelled when the component is torn down — this is what
   * previously saturated Chrome's 6-concurrent-connections-per-origin pool
   * and starved the Billing page's own HTTP calls on menu-click navigation.
   */
  private destroy$ = new Subject<void>();
  /**
   * Fires on every `NavigationEnd`. Background syncs re-triggered on each
   * navigation `switchMap`-cancel through this subject so a slow /next-step
   * from a prior route never races a newer one.
   */
  private navTrigger$ = new Subject<void>();

  readonly exploreStops: ExploreStopDef[] = [
    {
      id: 'screenshots',
      label: 'Your screenshots',
      hint: 'Real captures from this computer',
      icon: 'photo_library',
      route: '/screenshots',
    },
    {
      id: 'apps',
      label: 'Apps & sites',
      hint: 'Your test so far — plus an Example of a full day on this page',
      icon: 'apps',
      route: '/reports',
    },
    {
      id: 'hours',
      label: 'Work hours',
      hint: 'Your hours so far — plus an Example of a full day on this page',
      icon: 'schedule',
      route: '/user-report',
    },
    {
      id: 'work_replay',
      label: 'Work Replay',
      readyLabel: 'Work Replay',
      pendingHint: 'Collecting enough timeline data — use Example Workday meanwhile.',
      readyHint: 'Replay screenshots, apps, active time and idle gaps on one timeline.',
      icon: 'movie',
      route: '/work-replay',
    },
    {
      id: 'example_workday',
      label: 'See a full example workday',
      hint: 'The clearest view of what SuperSee shows for a real day',
      icon: 'menu_book',
      route: '/setup/example-workday',
    },
    {
      id: 'example_trends',
      label: 'Spot what repeats',
      hint: 'Example · Productivity Trends — not your company data',
      icon: 'insights',
      route: '/setup/example-workday',
    },
  ];

  stepIcons: { [key: string]: string } = {
    'mode_selection': 'settings',
    'create_employee': 'person_add',
    'install_agent': 'computer',
    'request_screenshot': 'photo_camera'
  };

  constructor(
    private onboardingService: OnboardingChecklistService,
    private activationJourney: ActivationJourneyService,
    private exampleWorkday: ExampleWorkdayService,
    private activationDestination: ActivationDestinationService,
    private router: Router,
    private tokenEncryptionService: TokenEncryptionService,
    private _sharedService: SharedService,
    private entitlementsService: EntitlementsService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    const initialUser = this.tokenEncryptionService.getEncryptedItem<any>('user');
    this.isCompanyAdmin = hasCompanyRole(initialUser);

    this.subscription = this.onboardingService.state$.subscribe(state => {
      setTimeout(() => {
        this.state = state;
        this.updateVisibility();
      });
    });

    this.journeySubscription = this.activationJourney.state$.subscribe(js => {
      setTimeout(() => {
        this.journeyState = js;
        this.exploreCollapsed = !!js.exploreGuideCollapsed;
        this.ownWorkdayReady = this.activationJourney.isOwnWorkdayReady();
        this.activationJourney.maybeTrackOwnWorkdayReady();
        this.updateVisibility();
      });
    });

    this.hideSessionSubscription = this.onboardingService.isHiddenForSession().subscribe(hidden => {
      setTimeout(() => {
        this.isHiddenForSession = hidden;
        this.updateVisibility();
      });
    });

    this.eligibilitySubscription = this.onboardingService.eligible$.subscribe(eligible => {
      setTimeout(() => {
        this.isEligible = eligible;
        this.updateVisibility();
      });
    });

    this.checkUserStatus();
    setTimeout(() => this.updateVisibility());

    // Single bounded pipe for background syncs: each `navTrigger$` tick
    // switches to a fresh sync that cancels any still-in-flight prior one,
    // the sync is `timeout(12000)`-bound so a hung /next-step cannot hold a
    // connection pool slot forever, and `takeUntil(destroy$)` ensures
    // in-flight HTTP is cancelled when the component is torn down (not that
    // it should be, since this component is global, but keeps things tidy).
    this.navTrigger$
      .pipe(
        switchMap(() => {
          if (!this.isLoggedIn) {
            return of(null);
          }
          return this.onboardingService.syncStateFromBackend().pipe(
            timeout(12000),
            catchError((err) => {
              console.warn('[FloatingChecklist] background sync skipped (timeout/err).', err);
              return of(null);
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    if (this.isLoggedIn) {
      // Initial detect+sync pair runs through its own bounded pipe to avoid
      // holding the connection pool if either endpoint hangs on fresh
      // accounts.
      setTimeout(() => {
        this.onboardingService
          .detectCompletedSteps()
          .pipe(
            timeout(12000),
            catchError((err) => {
              console.warn('[FloatingChecklist] detectCompletedSteps skipped (timeout/err).', err);
              return of(null);
            }),
            takeUntil(this.destroy$)
          )
          .subscribe({
            next: () => this.navTrigger$.next(),
            error: () => this.navTrigger$.next(),
          });
      }, 500);
    }

    this.currentRoute = this.router.url;
    this.noteExploreVisitFromUrl(this.currentRoute);
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        setTimeout(() => {
          this.currentRoute = event.urlAfterRedirects;
          this.checkUserStatus();
          this.noteExploreVisitFromUrl(this.currentRoute);
          // Fire through the bounded switchMap pipe above instead of
          // subscribing directly — cancels any prior in-flight sync.
          this.navTrigger$.next();
          this.updateVisibility();
        });
      });
  }

  private checkUserStatus(): void {
    const token = this.tokenEncryptionService.getToken();
    this.isLoggedIn = !!token;

    if (this.isLoggedIn) {
      const user = this.tokenEncryptionService.getEncryptedItem<any>('user');
      this.isCompanyAdmin = hasCompanyRole(user);
      if (user && user.meta && user.meta.quota_status) {
        const status = user.meta.quota_status.toLowerCase();
        this.isTrialUser = status === 'trial';
      } else {
        this.isTrialUser = false;
      }
      this.onboardingService.checkEligibility().subscribe();
    } else {
      this.isTrialUser = false;
    }
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.journeySubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
    this.hideSessionSubscription?.unsubscribe();
    this.eligibilitySubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    this.navTrigger$.complete();
  }

  get completedCount(): number {
    return this.onboardingService.getCompletedCount();
  }

  get totalSteps(): number {
    return this.onboardingService.getTotalSteps();
  }

  get progressPercent(): number {
    return (this.completedCount / this.totalSteps) * 100;
  }

  get isAllCompleted(): boolean {
    return this.onboardingService.isAllCompleted();
  }

  get setupCtaLabel(): string {
    return this.onboardingService.hasSetupStarted(this.state) ? 'Resume setup' : 'Start setup';
  }

  get exploreVisitedCount(): number {
    const visited = this.journeyState?.exploreVisited || [];
    // Denominator excludes pending/inaccessible Work Replay so founders aren't stuck at "3 of 4".
    return this.countableExploreStops.filter((s) => visited.includes(s.id)).length;
  }

  get countableExploreStops(): ExploreStopDef[] {
    return this.exploreStops.filter((s) => {
      if (!this.hasExploreablePrivateTest) {
        return s.id === 'example_workday' || s.id === 'example_trends';
      }
      if (s.id === 'work_replay') {
        return this.isWorkReplayNavigable;
      }
      return true;
    });
  }

  get allCoreStopsVisited(): boolean {
    const visited = new Set(this.journeyState?.exploreVisited || []);
    const required: ExploreStopId[] = [
      'screenshots',
      'apps',
      'hours',
      'example_workday',
      'example_trends',
    ];
    if (this.isWorkReplayNavigable) {
      required.splice(3, 0, 'work_replay');
    }
    return required.every((id) => visited.has(id));
  }

  get privateTestStops(): ExploreStopDef[] {
    return this.exploreStops.filter((s) => s.id === 'screenshots' || s.id === 'apps' || s.id === 'hours');
  }

  get exampleStops(): ExploreStopDef[] {
    return this.exploreStops.filter(
      (s) => s.id === 'example_workday' || s.id === 'example_trends'
    );
  }

  get workReplayStops(): ExploreStopDef[] {
    return this.exploreStops.filter((s) => s.id === 'work_replay');
  }

  /** Entitled + data-ready + date compatible with plan preview rules. */
  get isWorkReplayNavigable(): boolean {
    if (!this.entitlementsService.canAccessWorkReplay()) {
      return false;
    }
    if (!this.activationJourney.isWorkReplayGuideDataReady()) {
      return false;
    }
    if (this.entitlementsService.isWorkReplayPreviewOnly()) {
      const replayDate = this.activationJourney.privateTestReplayDateYmd();
      const yesterday = this.yesterdayYmd();
      return !!replayDate && replayDate === yesterday;
    }
    return true;
  }

  get workReplayCollecting(): boolean {
    return this.entitlementsService.canAccessWorkReplay() && !this.isWorkReplayNavigable;
  }

  private yesterdayYmd(): string {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  get showExploreOrientation(): boolean {
    if (this.isPausedCompanion && !this.hasExploreablePrivateTest) {
      return false;
    }
    return !this.exploreCollapsed;
  }

  get isPausedCompanion(): boolean {
    const js = this.journeyState;
    if (!js) {
      return false;
    }
    return js.journeyStatus === 'paused' || (!!js.continuedLater && !js.finished);
  }

  get hasExploreablePrivateTest(): boolean {
    const js = this.journeyState;
    if (!js) {
      return false;
    }
    return (
      (js.completedSteps || []).includes('private_proof') ||
      js.activationStatusPhase === 'first_data' ||
      js.privateEvaluationActive ||
      js.evaluationStatus === 'private_active'
    );
  }

  /** Paused before first screenshot — Example + Start private test only. */
  get isPreProofLookAround(): boolean {
    return this.isPausedCompanion && !this.hasExploreablePrivateTest;
  }

  get exploreGuideTitle(): string {
    if (this.hasExploreablePrivateTest) {
      return 'This computer';
    }
    return this.isPreProofLookAround ? 'See SuperSee' : 'See SuperSee';
  }

  get exploreGuideSubtitle(): string {
    if (this.hasExploreablePrivateTest) {
      return `${this.exploreVisitedCount} of ${this.countableExploreStops.length} stops`;
    }
    return 'An example workday · install on this computer when ready';
  }

  get exploreOrientCopy(): string {
    if (this.isPausedCompanion && !this.hasExploreablePrivateTest) {
      return '';
    }
    if (this.showExampleTrendsPanel) {
      return 'Patterns across days — example only. Real Productivity Trends needs more than one short test.';
    }
    const stop = this.activationJourney.exploreStopFromUrl(this.currentRoute);
    if (stop === 'screenshots') {
      return 'You’re on Screenshots — captures from this computer. Example workday shows a full day when data is thin.';
    }
    if (stop === 'apps') {
      return 'You’re on Apps & sites. Rows from this computer stay separate from Example workday.';
    }
    if (stop === 'hours') {
      return 'You’re on Work hours. Unknown values stay Collecting — never shown as 00:00.';
    }
    if (stop === 'work_replay') {
      return 'You’re on Work Replay — one timeline of screenshots, apps, active time, and idle gaps.';
    }
    if (stop === 'example_workday') {
      return 'Example workday · Not your company data. Exit returns to this computer.';
    }
    return 'This is your real admin view from this computer. Where a short install isn’t enough, Example shows a full workday.';
  }

  get explorePillLabel(): string {
    if (this.isPausedCompanion && !this.hasExploreablePrivateTest) {
      return 'See SuperSee';
    }
    const stop = this.activationJourney.exploreStopFromUrl(this.currentRoute);
    const next = this.nextUnvisitedCoreStop;
    if (stop === 'screenshots') {
      return next ? `Screenshots · next: ${next.label}` : 'Screenshots';
    }
    if (stop === 'apps') {
      return next ? `Apps · next: ${next.label}` : 'Apps & sites';
    }
    if (stop === 'hours') {
      return next ? `Hours · next: ${next.label}` : 'Work hours';
    }
    if (stop === 'work_replay') {
      return next ? `Replay · next: ${next.label}` : 'Work Replay';
    }
    return `Explore · ${this.exploreVisitedCount}/${this.countableExploreStops.length}`;
  }

  get exploreCtaSupport(): string {
    if (this.isPausedCompanion && !this.hasExploreablePrivateTest) {
      return '';
    }
    if (this.allCoreStopsVisited) {
      return 'One real workday is the next meaningful proof. We’ll help you introduce it fairly.';
    }
    if (this.workReplayCollecting && !this.isExploreStopVisited('example_workday')) {
      return 'Work Replay is still collecting. Example shows the full picture without involving anyone else.';
    }
    if (!this.isExploreStopVisited('example_workday') && !this.isWorkReplayNavigable) {
      return 'Your test data is real but short. Example shows the full picture without involving anyone else.';
    }
    return 'Keep reviewing this computer. When you’re ready, one real workday is the next meaningful proof.';
  }

  get explorePrimaryLabel(): string {
    if (this.isPreProofLookAround) {
      return 'Back to the example';
    }
    if (this.isPausedCompanion) {
      return this.resumeBannerCta;
    }
    if (this.allCoreStopsVisited) {
      return 'Try it with one employee';
    }
    const next = this.nextUnvisitedCoreStop;
    if (next) {
      return next.id === 'work_replay' && !this.isWorkReplayNavigable
        ? 'See a full example workday'
        : next.label;
    }
    return 'Try it with one employee';
  }

  get showQuietTryCta(): boolean {
    if (this.isPreProofLookAround) {
      return true;
    }
    if (this.isPausedCompanion && !this.hasExploreablePrivateTest) {
      return false;
    }
    // One-employee secondary remains visible throughout post-proof exploration.
    return !this.allCoreStopsVisited;
  }

  get quietTryCtaLabel(): string {
    return this.isPreProofLookAround ? 'Install on this computer' : 'Try it with one employee';
  }

  get nextUnvisitedCoreStop(): ExploreStopDef | null {
    const order: ExploreStopId[] = ['screenshots', 'apps', 'hours'];
    for (const id of order) {
      if (!this.isExploreStopVisited(id)) {
        return this.exploreStops.find((s) => s.id === id) || null;
      }
    }
    if (this.isWorkReplayNavigable && !this.isExploreStopVisited('work_replay')) {
      return this.exploreStops.find((s) => s.id === 'work_replay') || null;
    }
    if (!this.isExploreStopVisited('example_workday')) {
      return this.exploreStops.find((s) => s.id === 'example_workday') || null;
    }
    if (!this.isExploreStopVisited('example_trends')) {
      return this.exploreStops.find((s) => s.id === 'example_trends') || null;
    }
    return null;
  }

  isExploreStopVisited(id: ExploreStopId): boolean {
    return (this.journeyState?.exploreVisited || []).includes(id);
  }

  stopLabel(stop: ExploreStopDef): string {
    if (stop.id === 'work_replay' && !this.isWorkReplayNavigable) {
      return 'Work Replay · Collecting';
    }
    if (stop.id === 'work_replay' && stop.readyLabel) {
      return stop.readyLabel;
    }
    return stop.label;
  }

  stopHint(stop: ExploreStopDef): string {
    if (stop.id === 'work_replay') {
      return this.isWorkReplayNavigable
        ? stop.readyHint || ''
        : stop.pendingHint || '';
    }
    return stop.hint || '';
  }

  private noteExploreVisitFromUrl(url: string): void {
    if (!this.activationJourney.isEnabled) {
      return;
    }
    if (!this.journeyState?.privateEvaluationActive && !this.isPausedCompanion) {
      return;
    }
    const stop = this.activationJourney.exploreStopFromUrl(url);
    if (stop) {
      if (stop !== 'example_trends' && this.showExampleTrendsPanel) {
        this.showExampleTrendsPanel = false;
      }
      this.activationJourney.markExploreVisited(stop);
    }
  }

  goExploreStop(stop: ExploreStopDef): void {
    this.orientationSeen = true;
    if (stop.id !== 'example_trends') {
      this.showExampleTrendsPanel = false;
    }
    if (stop.id === 'work_replay' && !this.isWorkReplayNavigable) {
      return;
    }
    if (stop.id === 'example_trends') {
      this.activationJourney.markExploreVisited('example_trends');
      this.showExampleTrendsPanel = false;
      this.exampleWorkday.openGuidedExample({ fromProof: false, step: 'trends' });
      this.cdr.markForCheck();
      return;
    }
    if (stop.id === 'example_workday') {
      this.activationJourney.markExploreVisited('example_workday');
      this.exampleWorkday.openGuidedExample({ fromProof: false });
      return;
    }
    const employeeId = this.journeyState?.privateTestEmployeeId;
    if (stop.id === 'screenshots' && employeeId) {
      void this.router.navigate(['/user-details', employeeId]);
      return;
    }
    if (stop.id === 'work_replay') {
      const queryParams: Record<string, string> = {};
      if (employeeId) {
        queryParams['employeeId'] = String(employeeId);
      }
      const date = this.activationJourney.privateTestReplayDateYmd();
      if (date) {
        queryParams['date'] = date;
      }
      this.activationJourney.markExploreVisited('work_replay');
      void this.router.navigate(['/work-replay'], { queryParams });
      return;
    }
    // Timesheet deep-link contract: ?ts=user&uid=… (not employeeId — that is screenshots/profile).
    const queryParams =
      stop.id === 'hours'
        ? employeeId
          ? { ts: 'user', uid: String(employeeId) }
          : { ts: 'user' }
        : {};
    if (stop.route) {
      void this.router.navigate([stop.route], { queryParams });
    }
  }

  hideExampleTrendsPanel(): void {
    this.showExampleTrendsPanel = false;
    this.cdr.markForCheck();
  }

  onExplorePrimaryCta(): void {
    this.orientationSeen = true;
    if (this.isPreProofLookAround) {
      this.exampleWorkday.openFromUnderstand();
      return;
    }
    if (this.isPausedCompanion) {
      this.resumeJourneySetup();
      return;
    }
    if (this.allCoreStopsVisited) {
      this.tryWithOneEmployee();
      return;
    }
    const next = this.nextUnvisitedCoreStop;
    if (next) {
      if (next.id === 'work_replay' && !this.isWorkReplayNavigable) {
        this.activationJourney.markExploreVisited('example_workday');
        this.exampleWorkday.openGuidedExample({ fromProof: false });
        return;
      }
      this.goExploreStop(next);
      return;
    }
    this.tryWithOneEmployee();
  }

  expandExploreGuide(): void {
    this.exploreCollapsed = false;
    this.activationJourney.setExploreGuideCollapsed(false);
    this.cdr.markForCheck();
  }

  collapseExploreGuide(): void {
    this.orientationSeen = true;
    this.exploreCollapsed = true;
    this.showExampleTrendsPanel = false;
    this.activationJourney.setExploreGuideCollapsed(true);
    this.cdr.markForCheck();
  }

  toggleWhatYouControl(): void {
    this.showWhatYouControl = !this.showWhatYouControl;
    this.cdr.markForCheck();
  }

  private updateVisibility(): void {
    const journeyOn = this.activationJourney.isEnabled;
    const js = this.journeyState;
    const privateEval = !!(
      journeyOn &&
      (js?.privateEvaluationActive || js?.evaluationStatus === 'private_active')
    );
    const continuedLater = !!(
      journeyOn &&
      (js?.journeyStatus === 'paused' || (js?.continuedLater && !js?.finished))
    );

    if (!this.isCompanyAdmin) {
      this.shouldShow = false;
      this.showPrivateEvalBanner = false;
      this.showJourneyResumeBanner = false;
      this.resumeDestination = null;
      this.cdr.markForCheck();
      return;
    }

    // Finished / skipped journey: no setup chrome.
    if (journeyOn && this.activationJourney.isFinishedOrSkipped()) {
      this.shouldShow = false;
      this.showPrivateEvalBanner = false;
      this.showJourneyResumeBanner = false;
      this.resumeDestination = null;
      this.cdr.markForCheck();
      return;
    }

    // Continue later: same right-side card as post-proof explore, not a bottom strip.
    // Keep paused (don't flip private_active) so they stay in the real admin.
    const onExampleWorkday = this.currentRoute.includes('example-workday');
    if (
      journeyOn &&
      continuedLater &&
      (!this.currentRoute.startsWith('/setup') || onExampleWorkday)
    ) {
      const hiddenRoutes = ['/login', '/signup', '/forgot-password', '/verification-code', '/onboarding'];
      const isHiddenRoute = hiddenRoutes.some(
        (route) => this.currentRoute === route || this.currentRoute.startsWith(route + '/')
      );
      // First employee already connected (Done for now) — collecting is not unfinished setup.
      if (isFirstEmployeeCollectingPause(js)) {
        this.showJourneyResumeBanner = false;
        this.showPrivateEvalBanner = false;
        this.resumeDestination = null;
        this.shouldShow = false;
        this.cdr.markForCheck();
        return;
      }
      this.resumeDestination = this.activationDestination.resolveResume(
        js || this.activationJourney.getState()
      );
      this.showPrivateEvalBanner = this.isLoggedIn && !isHiddenRoute;
      this.showJourneyResumeBanner = false;
      this.shouldShow = this.showPrivateEvalBanner;
      if (
        this.shouldShow &&
        this.isPreProofLookAround &&
        !this.explorePromptShownTracked &&
        typeof this.activationJourney.trackEvent === 'function'
      ) {
        this.explorePromptShownTracked = true;
        this.activationJourney.trackEvent('explore_prompt_shown', { source: 'understand' });
      }
      this.cdr.markForCheck();
      return;
    }

    // Explore guide while founder evaluates — never show resume bar at the same time.
    if (privateEval) {
      const hiddenRoutes = ['/login', '/signup', '/forgot-password', '/verification-code', '/onboarding'];
      const isHiddenRoute = hiddenRoutes.some(
        (route) => this.currentRoute === route || this.currentRoute.startsWith(route + '/')
      );
      // Hide on /setup except example-workday (still exploration).
      const onSetup = this.currentRoute.startsWith('/setup');
      const onExampleWorkday = this.currentRoute.includes('example-workday');
      this.showPrivateEvalBanner =
        this.isLoggedIn && !isHiddenRoute && (!onSetup || onExampleWorkday);
      this.showJourneyResumeBanner = false;
      this.resumeDestination = null;
      this.shouldShow = this.showPrivateEvalBanner;
      this.cdr.markForCheck();
      return;
    }

    this.showPrivateEvalBanner = false;
    this.showJourneyResumeBanner = false;
    this.resumeDestination = null;

    const isPermanentlyCompleted = localStorage.getItem('supersee_onboarding_completed') === 'true';
    if (isPermanentlyCompleted) {
      this.shouldShow = false;
      this.cdr.markForCheck();
      return;
    }

    // Guided journey uses /setup — hide classic floating checklist there.
    if (journeyOn) {
      this.shouldShow = false;
      this.cdr.markForCheck();
      return;
    }

    if (this.currentRoute.startsWith('/setup')) {
      this.shouldShow = false;
      this.cdr.markForCheck();
      return;
    }

    if (!this.isLoggedIn) {
      this.shouldShow = false;
      this.cdr.markForCheck();
      return;
    }

    const hiddenRoutes = ['/login', '/signup', '/forgot-password', '/verification-code', '/onboarding'];
    const isHiddenRoute = hiddenRoutes.some(route => this.currentRoute === route || this.currentRoute.startsWith(route + '/'));

    if (this.currentRoute.startsWith('/dashboard') || !isHiddenRoute) {
      this.shouldShow = this.isEligible && this.state && !this.isAllCompleted && !this.isHiddenForSession;
      this.cdr.markForCheck();
      return;
    }

    this.shouldShow = false;
    this.cdr.markForCheck();
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded;
    this.onboardingService.setPanelExpanded(this.expanded);
  }

  toggleMinimized(): void {
    this.onboardingService.toggleMinimized();
  }

  private getQueryParamForStep(stepId?: string): string {
    if (stepId === 'create_employee') return 'intro';
    if (stepId === 'install_agent') return 'download';
    if (stepId === 'request_screenshot') return 'download';
    return 'intro';
  }

  goToStep(index: number): void {
    if (index >= 0 && index < this.state.steps.length) {
      this.onboardingService.goToStep(index);
      const stepId = this.state.steps[index].id;
      this.router.navigate(['/setup'], { queryParams: { step: this.getQueryParamForStep(stepId) } });
    }
  }

  continueSetup(): void {
    if (this.state && this.state.skipped) {
      this.onboardingService.resumeOnboarding();
    }
    const currentIndex = this.state?.currentStepIndex || 0;
    const stepId = this.state?.steps[currentIndex]?.id;
    this.router.navigate(['/setup'], { queryParams: { step: this.getQueryParamForStep(stepId) } });
  }

  skipOnboarding(): void {
    this.onboardingService.skipOnboarding();
    this.expanded = false;
    this.onboardingService.setPanelExpanded(false);
    this._sharedService.isOpenCloseSidebar.next({ force: true });
    this.router.navigate(['/dashboard']);
  }

  resumeOnboarding(): void {
    const currentIndex = this.state?.currentStepIndex || 0;
    const stepId = this.state?.steps[currentIndex]?.id;
    this.router.navigate(['/setup'], { queryParams: { step: this.getQueryParamForStep(stepId) } }).then(success => {
      if (success) {
        this.onboardingService.resumeOnboarding();
      }
    });
  }

  openPrivateTestScreenshots(): void {
    const employeeId = this.journeyState?.privateTestEmployeeId;
    if (employeeId) {
      void this.router.navigate(['/screenshots'], { queryParams: { employeeId: String(employeeId) } });
      return;
    }
    void this.router.navigate(['/screenshots']);
  }

  onQuietTryCta(): void {
    if (this.isPreProofLookAround) {
      this.activationJourney.trackEvent('explore_prompt_accepted', { source: 'understand' });
      this.activationJourney.clearContinueLater();
      this.activationJourney.startPrivateTestFromReady();
      void this.router.navigate(['/setup/install']);
      this.activationJourney.flushServerSync().subscribe({ error: () => {} });
      return;
    }
    this.tryWithOneEmployee();
  }

  tryWithOneEmployee(): void {
    this.activationJourney.resumeFromPrivateEvaluation();
    const navigateResolved = (): void => {
      const dest = this.activationDestination.resolve({
        state: this.activationJourney.getState(),
      });
      void this.router.navigate([dest.route], { queryParams: dest.queryParams || {} });
    };
    this.activationJourney.flushServerSync().subscribe({
      next: () => navigateResolved(),
      error: () => navigateResolved(),
    });
  }

  get resumeBannerTitle(): string {
    const state = this.activationJourney.getState();
    if (state.waitingReason === 'employee_data' && state.resumeDestination === 'first_employee') {
      return 'First signals are collecting';
    }
    return this.resumeDestination?.ctaLabel || 'Resume setup';
  }

  get resumeBannerBody(): string {
    const state = this.activationJourney.getState();
    if (state.waitingReason === 'employee_data' && state.resumeDestination === 'first_employee') {
      return (
        this.resumeDestination?.reason ||
        'SuperSee is working on their computer. Return to check whether the first review is ready.'
      );
    }
    return this.resumeDestination?.reason || 'Your activation progress is saved. Resume when you are ready to continue.';
  }

  get resumeBannerCta(): string {
    const state = this.activationJourney.getState();
    if (state.waitingReason === 'employee_data' && state.resumeDestination === 'first_employee') {
      return 'Check first signals';
    }
    return this.resumeDestination?.ctaLabel || 'Resume setup';
  }

  resumeJourneySetup(): void {
    this.activationJourney.clearContinueLater();
    const dest =
      this.resumeDestination ||
      this.activationDestination.resolveResume(this.activationJourney.getState());
    void this.router.navigate([dest.route], { queryParams: dest.queryParams || {} });
  }

  downloadRemovalFromBanner(): void {
    const link = document.createElement('a');
    link.href = 'assets/Windows-Rust/Remove/Remove_SuperSee.bat';
    link.download = 'Remove_SuperSee.bat';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    this.activationJourney.trackEvent('removal_utility_downloaded', { source: 'private_eval_banner' });
  }

  bookDemo(): void {
    window.open('https://calendly.com/raoinformationtechnology/supersee-demo', '_blank');
  }

  // Support WhatsApp contact ≠ Daily Insights WhatsApp Summary (product gated off).
  contactSupport(): void {
    window.open('https://api.whatsapp.com/send/?phone=916354719703&text=Hi%2C+I+need+help+in+onboarding.&type=phone_number&app_absent=0', '_blank');
  }
}
